/**
 * ViewCollectionSetModal
 *
 * Modal for viewing and managing the Collection Set (endpoints tagged for collection).
 * Thin wrapper around EndpointSetModal with collection-set specific copy and visuals.
 */

import type { ReactElement } from 'react'
import { FolderInput } from 'lucide-react'
import type { CaseEndpoint } from '../../types/case-container'
import { EndpointSetModal } from './EndpointSetModal'
import type { EndpointSetCopy, EndpointSetVisuals } from './endpoint-set-modal.parts'

interface ViewCollectionSetModalProps {
  collectionSetIds: Set<string>
  endpoints: CaseEndpoint[]
  onClose: () => void
  onRemove: (endpointId: string) => void
  onRemoveMany: (endpointIds: string[]) => void
  onClear: () => void
}

const VISUALS: EndpointSetVisuals = {
  icon: FolderInput,
  iconBgClass: 'bg-fuchsia-100 dark:bg-fuchsia-950/50',
  iconColorClass: 'text-fuchsia-600 dark:text-fuchsia-400',
}

const COPY: EndpointSetCopy = {
  titleKey: 'infrastructure.collectionSet.title',
  endpointCountKey: 'infrastructure.collectionSet.endpointCount',
  noSearchResultsKey: 'infrastructure.collectionSet.noSearchResults',
  noEndpointsYetKey: 'infrastructure.collectionSet.noEndpointsYet',
  addHintKey: 'infrastructure.collectionSet.addHint',
  selectAllKey: 'infrastructure.collectionSet.selectAll',
  showingCountKey: 'infrastructure.collectionSet.showingCount',
  filteredKey: 'infrastructure.collectionSet.filtered',
  clearAllConfirmKey: 'infrastructure.collectionSet.clearAllConfirm',
  yesClearKey: 'infrastructure.collectionSet.yesClear',
  clearAllKey: 'infrastructure.collectionSet.clearAll',
  removeTooltipKey: 'infrastructure.removeFromCollectionSet',
}

export function ViewCollectionSetModal({
  collectionSetIds,
  endpoints,
  onClose,
  onRemove,
  onRemoveMany,
  onClear,
}: ViewCollectionSetModalProps): ReactElement {
  return (
    <EndpointSetModal
      endpointIds={collectionSetIds}
      endpoints={endpoints}
      visuals={VISUALS}
      copy={COPY}
      onClose={onClose}
      onRemove={onRemove}
      onRemoveMany={onRemoveMany}
      onClear={onClear}
    />
  )
}
